import { unsafeCoerce } from 'fp-ts/lib/function';
import { InputHTMLAttributes, memo, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../../../react-i18next';
import { applyFormFieldInteraction } from '../../../AnalyticsContext/applyFormFieldInteraction';
import { AsteriskIcon, CloseIcon, Input, Label, TickIcon, Wrapper } from './TextField.styles';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  required?: boolean;
  complete?: boolean;
  error?: boolean;
  labelKey?: string;
  value?: string;
  name?: string;
  disabled?: boolean;
  label?: string;
  fieldRef?: RefObject<HTMLInputElement>;
}

export const TextField = memo((props: TextFieldProps) => {
  const { t } = useTranslation();

  const {
    required,
    complete,
    error,
    labelKey,
    className,
    value,
    autoComplete,
    onChange,
    name,
    disabled,
    label: initialLabel,
    fieldRef,
    ...inputProps
  } = props;

  const hasValue = value !== undefined && value.length > 0;

  const showAsterisk = required && !complete && !error;
  const showTick = complete && !error;
  const iconVisible = showAsterisk || showTick || error;
  const placeholder = inputProps.placeholder;

  const label: string =
    t(unsafeCoerce<string | undefined, TranslationKey>(labelKey)) || labelKey || initialLabel;

  return (
    <Wrapper className={className} {...applyFormFieldInteraction(name, value)}>
      <Input
        {...inputProps}
        onChange={onChange}
        value={value}
        iconVisible={iconVisible}
        labelVisible={Boolean(label)}
        error={error}
        aria-label={props['aria-label'] || label || placeholder}
        aria-required={required}
        name={name}
        autoComplete={autoComplete}
        dir="ltr"
        disabled={disabled}
        ref={fieldRef}
      />
      <Label hasValue={hasValue} error={error} htmlFor={props.id}>
        {label}
      </Label>
      {showAsterisk && <AsteriskIcon aria-hidden="true" />}
      {showTick && <TickIcon />}
      {error && <CloseIcon />}
    </Wrapper>
  );
});
